import React, { useEffect, useState, useRef } from "react";
import { NavLink, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ButtonGroup, Button, InputGroup, InputGroupText, Input, Col, CardBody, Card, Row, Table, Badge } from "reactstrap";
import './Order.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCommentAlt, faBicycle, faWalking, faMapMarkerAlt, faCheckCircle, faShoppingBasket, faEnvelope, faSearch, faTruck, faFire, faClipboardList, faClock, faPhone, faMoneyBill, faUser, faBuilding, faCalendarAlt, faCreditCard, faShippingFast, faInfoCircle, faLink } from '@fortawesome/free-solid-svg-icons';
import moment from 'moment';
import DiscountCouponService from "../DiscountCoupon/Discountcoupons.service.js";
import AuthService from "../Authentication/auth.service.js";
import LoadingOverlay from "react-loading-overlay-ts";
import DNALoader from "../../components/Loaders/DnaLoader.js";
import PermissionAccess from '../../PermissionAccess/Access.js';
import Alerts from "../../components/Alerts/alerts.js";
import { faPaperPlane } from '@fortawesome/free-solid-svg-icons';

const OrderDetails = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const { id } = useParams();
    const [activeTab, setActiveTab] = useState('details');
    const { authData } = PermissionAccess();
    const [orderData, setOrderData] = useState(null);
    const userDetails = JSON.parse(localStorage.getItem("user"));
    const [messages, setMessages] = useState([]);
    const [newMessage, setNewMessage] = useState('');

    useEffect(() => {
        fetchOrderDetails();
    }, [id, authData.sessionID]);

    useEffect(() => {
        if (orderData) {
            fetchMessages();
        }
    }, [orderData, authData.sessionID]);

    const chatContainerRef = useRef(null);

    useEffect(() => {
        if (chatContainerRef.current) {
            chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
        }
    }, [messages]);

    const fetchOrderDetails = async () => {
        try {
            const response = await DiscountCouponService.GetUser_OrderDetails(authData?.sessionID ? authData?.sessionID : userDetails?.sessionID, id);
            if (response.data.message === "Unauthorized") {
                AuthService.logout();
                navigate("/login");
                window.location.reload();
            } else if (response.data && response.data.status === true) {
                // Handle the new API response structure
                const orderDataFromAPI = response.data.orderData[0];
                setOrderData(orderDataFromAPI);
            }
        } catch (error) {
            console.error("Error fetching order details:", error);
        } finally {
            setLoading(false);
        }
    };

    const fetchMessages = async () => {
        try {
            const orderId = orderData?.orderDetails?._id || orderData?.order_id || id;
            const response = await DiscountCouponService.GetOrderMessages(authData.sessionID, orderId, 'admin');
            if (response.data.message === "Unauthorized") {
                AuthService.logout();
                navigate("/login");
                window.location.reload();
            } else if (response.data) {
                setMessages(response.data.messages);
            }
        } catch (error) {
            console.error("Error fetching messages:", error);
        }
    };

    const sendMessage = async () => {
        if (newMessage.trim() === '') return;

        try {
            const orderId = orderData?.orderDetails?._id || orderData?.order_id || id;
            await DiscountCouponService.SendOrderMessage(authData.sessionID, orderId, newMessage, 'admin');
            setNewMessage('');
            fetchMessages();
        } catch (error) {
            console.error("Error sending message:", error);
        }
    };

    const renderMessagingTab = () => (
        <Card className="main-card mb-3">
            <CardBody>
                <h5 className="card-title mb-3">
                    <FontAwesomeIcon icon={faCommentAlt} style={{ color: '#3989A3' }} className="me-2" />
                    Order Communication
                </h5>
                <div className="chat-container">
                    <div
                        className="messages-container"
                        ref={chatContainerRef}
                    >
                        {messages?.map((msg, index) => (
                            <div key={index} className={`message-wrapper ${msg.sender_type === 'admin' ? 'sent' : 'received'}`}>
                                <div className="message-content">
                                    <div className="message-sender">
                                        <FontAwesomeIcon icon={msg.sender_type === 'admin' ? faUser : faBuilding} className="me-1" />
                                        {msg.sender_type === 'admin' ? 'Admin' : 'Customer'}
                                    </div>
                                    <div className="message-text">
                                        {msg.message}
                                    </div>
                                    <div className="message-time">
                                        {moment(msg.date).format('DD-MM-YYYY, h:mm A')}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                    <div className="message-input">
                        <input
                            type="text"
                            value={newMessage}
                            onChange={(e) => setNewMessage(e.target.value)}
                            placeholder="Type a message..."
                            onKeyPress={(e) => e.key === 'Enter' && sendMessage()}
                        />
                        <Button style={{ backgroundColor: '#3989A3', borderColor: '#3989A3' }} onClick={sendMessage}>
                            <FontAwesomeIcon icon={faPaperPlane} />
                        </Button>
                    </div>
                </div>
            </CardBody>
        </Card>
    );

    const formatStatus = (status) => {
        if (!status) return '';

        return status
            .toLowerCase()
            .split('_')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ');
    };

    const getStatusBadgeColor = (status) => {
        const statusLower = status?.toLowerCase();
        switch (statusLower) {
            case 'pending':
                return 'warning';
            case 'confirmed':
                return 'info';
            case 'processing':
                return 'primary';
            case 'shipped':
                return 'info';
            case 'delivered':
                return 'success';
            case 'cancelled':
                return 'danger';
            default:
                return 'secondary';
        }
    };

    const renderTrackingContentDEMO = () => {
        const trackingDetails = orderData?.orderTrackingDetails || [];
        
        if (!orderData || trackingDetails.length === 0) {
            return (
                <Card className="main-card mb-3">
                    <CardBody className="text-center py-5">
                        <FontAwesomeIcon icon={faInfoCircle} style={{ color: '#5689A2' }} style={{ fontSize: '3rem' }} />
                        <h5 className="mt-3 text-muted">No tracking information available</h5>
                        <p className="text-muted">Tracking details will appear here once the order is processed.</p>
                    </CardBody>
                </Card>
            );
        }

        return (
            <Card className="main-card mb-3">
                <CardBody>
                    <h5 className="card-title mb-4">
                        <FontAwesomeIcon icon={faShippingFast} style={{ color: '#3989A3' }} className="me-2" />
                        Order Tracking Timeline
                    </h5>
                    <div className="tracking-timeline">
                        {trackingDetails.map((step, index) => (
                            <div key={index} className="tracking-step">
                                <div className="tracking-icon">
                                    <FontAwesomeIcon icon={getIconForStatus(step.status)} />
                                </div>
                                <div className="tracking-content">
                                    <h6 className="tracking-status">{formatStatus(step.status)}</h6>
                                    <p className="tracking-time">
                                        <FontAwesomeIcon icon={faClock} className="me-2" />
                                        {moment(step.date_time).format('DD-MM-YYYY, h:mm:ss A')}
                                    </p>
                                </div>
                                {index !== trackingDetails.length - 1 && (
                                    <div className="tracking-line" />
                                )}
                            </div>
                        ))}
                    </div>
                </CardBody>
            </Card>
        );
    };

    const getIconForStatus = (status) => {
        switch (status.toLowerCase()) {
            case 'delivered':
                return faCheckCircle;
            case 'picked by driver':
                return faShoppingBasket;
            case 'driver assigned':
                return faBicycle;
            case 'on going':
                return faFire;
            case 'assigning driver':
                return faSearch;
            case 'order received':
                return faClipboardList;
            default:
                return faClipboardList;
        }
    };

    const processOrder = async (e, status) => {
        e.preventDefault();
        const msg = status
            ? t("You want to place order with Lalamove")
            : t("You want to place order without Lalamove");
        Alerts.swalOKAlert(msg).then(async res => {
            if (res === true) {
                setLoading(true);
                const orderId = orderData?.orderDetails?._id || orderData?.order_id || id;
                const response = await DiscountCouponService.AdminProcessOrder(authData.sessionID, orderId, status);
                setLoading(false);
                if (response.data.status === true) {
                    Alerts.swalClickProceedAlert('Order Has Been Placed Successfully').then(res => {
                        if (res === true) {
                            window.location.reload();
                        }
                    });
                }
            }
        });
    };

    const cancelLalamove = async (e, status) => {
        e.preventDefault();
        const msg = t("You want to cancel order from Lalamove")
        Alerts.swalOKAlert(msg).then(async res => {
            if (res === true) {
                setLoading(true);
                const orderId = orderData?.orderDetails?._id || orderData?.order_id || id;
                const response = await DiscountCouponService.CancelLalamoveOrder(authData.sessionID, orderId);
                setLoading(false);
                if (response.data.status === true) {
                    Alerts.swalClickProceedAlert('Order Has Been Cancelled Successfully').then(res => {
                        if (res === true) {
                            window.location.reload();
                        }
                    });
                }
            }
        });
    };

    const [inputValue, setInputValue] = useState('');
    const handleInputChange = (e) => {
        setInputValue(e.target.value);
    };

    const applyTip = async () => {
        if (isNaN(parseFloat(inputValue)) || parseFloat(inputValue) <= parseFloat(orderData?.priorityTip || 0)) {
            Alerts.swalErrorAlert(`The new tip must be greater than the previous tip ${orderData?.priorityTip || 0}.`);
            return;
        }
        Alerts.swalOKAlert('You want to apply this tip?').then(async res => {
            if (res === true) {
                setLoading(true);
                const orderId = orderData?.orderDetails?._id || orderData?.order_id || id;
                const response = await DiscountCouponService.AdditionalTip(authData.sessionID, orderId, inputValue);
                setLoading(false);
                if (response.data.status === true) {
                    Alerts.swalClickProceedAlert('Tip Has Been Applied Successfully').then(res => {
                        if (res === true) {
                            window.location.reload();
                        }
                    });
                }
            }
        });
    };

    const [inputValueLink, setInputValueLink] = useState('');
    const handleInputLinkChange = (e) => {
        setInputValueLink(e.target.value);
    };

    const applyLink = async () => {
        if (isNaN(parseFloat(inputValueLink))) {
            Alerts.swalErrorAlert(`Lalamove ID required.`);
            return;
        }
        Alerts.swalOKAlert('You want to link this ID?').then(async res => {
            if (res === true) {
                setLoading(true);
                const orderId = orderData?.orderDetails?._id || orderData?.order_id || id;
                const response = await DiscountCouponService.LalamoveLinkID(authData.sessionID, orderId, inputValueLink);
                setLoading(false);
                if (response.data.status === true) {
                    Alerts.swalClickProceedAlert('Lalamove order id updated successfully').then(res => {
                        if (res === true) {
                            window.location.reload();
                        }
                    });
                }
            }
        });
    };

    const additionalTip = async (e, amount) => {
        e.preventDefault();

        Alerts.swalOKAlert('You want to apply this tip?').then(async res => {
            if (res === true) {
                setLoading(true);
                const orderId = orderData?.orderDetails?._id || orderData?.order_id || id;
                const response = await DiscountCouponService.AdditionalTip(authData.sessionID, orderId, amount);
                setLoading(false);
                if (response.data.status === true) {
                    Alerts.swalClickProceedAlert('Tip Has Been Applied Successfully').then(res => {
                        if (res === true) {
                            window.location.reload();
                        }
                    });
                }
            }
        });
    };

    const renderOrderHeader = () => (
        <Card className="main-card mb-3">
            <CardBody>
                <Row className="g-3">
                    <Col md="3">
                        <div className="order-info-card">
                            <div className="order-info-icon">
                                <FontAwesomeIcon icon={faClipboardList} style={{ color: '#3989A3' }} />
                            </div>
                            <div className="order-info-content">
                                <h6 className="order-info-label">Order Number</h6>
                                <p className="order-info-value">{orderData?.orderDetails?.order_number || orderData?.order_number || 'N/A'}</p>
                            </div>
                        </div>
                    </Col>
                    <Col md="3">
                        <div className="order-info-card">
                            <div className="order-info-icon">
                                <FontAwesomeIcon icon={faClock} style={{ color: '#3D5F7A' }} />
                            </div>
                            <div className="order-info-content">
                                <h6 className="order-info-label">Status</h6>
                                <Badge color={getStatusBadgeColor(orderData?.orderDetails?.status || orderData?.status)} className="order-status-badge">
                                    {formatStatus(orderData?.orderDetails?.status || orderData?.status)}
                                </Badge>
                            </div>
                        </div>
                    </Col>
                    <Col md="3">
                        <div className="order-info-card">
                            <div className="order-info-icon">
                                <FontAwesomeIcon icon={(orderData?.orderDetails?.deliveryMethod || orderData?.deliveryMethod) === 'delivery' ? faBicycle : faWalking} style={{ color: '#5689A2' }} />
                            </div>
                            <div className="order-info-content">
                                <h6 className="order-info-label">{(orderData?.orderDetails?.deliveryMethod || orderData?.deliveryMethod) === 'delivery' ? 'Delivery' : 'Self Pickup'}</h6>
                                <Badge color={orderData?.late_status === true ? 'danger' : 'success'}>
                                    {orderData?.late_status === true ? 'Late' : 'Normal'}
                                </Badge>
                            </div>
                        </div>
                    </Col>
                    <Col md="3">
                        <div className="order-info-card">
                            <div className="order-info-icon">
                                <FontAwesomeIcon icon={faCalendarAlt} style={{ color: '#3989A3' }} />
                            </div>
                            <div className="order-info-content">
                                <h6 className="order-info-label">Order Date</h6>
                                <p className="order-info-value">{moment(orderData?.orderDetails?.order_date || orderData?.order_date).format('DD MMM YYYY')}</p>
                                <small className="text-muted">{moment(orderData?.orderDetails?.order_date || orderData?.order_date).format('h:mm A')}</small>
                            </div>
                        </div>
                    </Col>
                </Row>

                {(orderData?.late_status === true && orderData?.lalamove_orderID && (orderData?.orderDetails?.status || orderData?.status) === 'confirmed') && (
                    <Row className="mt-4">
                        <Col md="6">
                            <div className="priority-section">
                                <h6 className="priority-title">
                                    <FontAwesomeIcon icon={faMoneyBill} style={{ color: '#3D5F7A' }} className="me-2" />
                                    Priority Enhancement
                                </h6>
                                <InputGroup>
                                    <Input
                                        placeholder="Enter priority amount"
                                        value={inputValue}
                                        onChange={handleInputChange}
                                        className="priority-input"
                                    />
                                    <InputGroupText addonType="append">
                                        <Button style={{ backgroundColor: '#3D5F7A', borderColor: '#3D5F7A' }} onClick={applyTip}>Apply</Button>
                                    </InputGroupText>
                                </InputGroup>
                            </div>
                        </Col>
                        <Col md="6">
                            <div className="quick-tip-section">
                                <h6 className="quick-tip-title">Quick Tips</h6>
                                <ButtonGroup className="quick-tip-buttons">
                                    {['1.00', '2.00', '3.00', '4.00', '10.00', '20.00'].map((value) => (
                                        <Button
                                            key={value}
                                            style={{ 
                                                backgroundColor: 'transparent', 
                                                borderColor: '#5689A2', 
                                                color: '#5689A2' 
                                            }}
                                            size="sm"
                                            onClick={(e) => additionalTip(e, value)}
                                            disabled={parseFloat(value) <= parseFloat(orderData?.priorityTip || 0)}
                                        >
                                            RM {value}
                                        </Button>
                                    ))}
                                </ButtonGroup>
                            </div>
                        </Col>
                    </Row>
                )}
            </CardBody>
        </Card>
    );

    const renderOrderDetails = () => (
        <Card className="main-card mb-3">
            <CardBody>
                <h5 className="card-title mb-3">
                    <FontAwesomeIcon icon={faClipboardList} style={{ color: '#3989A3' }} className="me-2" />
                    Order Items
                </h5>
                <div className="table-responsive">
                    <Table className="order-items-table">
                        <thead>
                            <tr>
                                <th>{t("Item(s) - Brand")}</th>
                                <th>{t("Quantity")}</th>
                                <th>{t("Unit Price")}</th>
                                <th>{t("Batches")}</th>
                                <th>{t("Expiry Date")}</th>
                                <th>{t("Total Price")}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {renderItemRows(orderData?.orderDetails?.items || [])}
                        </tbody>
                    </Table>
                </div>
            </CardBody>
        </Card>
    );

    const renderItemRows = (items) => {
        if (!items || items.length === 0) {
            return (
                <tr>
                    <td colSpan="6" className="text-center text-muted py-4">
                        No items found
                    </td>
                </tr>
            );
        }

        return items.map((item, itemIndex) => {
            const batches = item?.selectedBatches || [];
            
            if (batches.length === 0) {
                return (
                    <tr key={`${itemIndex}-no-batch`} className="order-item-row">
                        <td className="item-cell">
                            <div className="d-flex align-items-center">
                                <div>
                                    <div className="item-name">{item?.item_name || 'N/A'}</div>
                                    <small className="text-muted">{item?.ItemGroup || 'No Brand'}</small>
                                    <br />
                                    <small className="text-muted">Code: {item?.item_code || 'N/A'}</small>
                                </div>
                            </div>
                        </td>
                        <td className="quantity-cell">
                            <Badge style={{ backgroundColor: '#5689A2' }} className="quantity-badge">{item?.quantity || 0}</Badge>
                        </td>
                        <td className="price-cell">
                            <span className="price-value">RM {parseFloat(item?.price || 0).toFixed(2)}</span>
                        </td>
                        <td className="batch-cell">
                            <span className="text-muted">-</span>
                        </td>
                        <td className="expiry-cell">
                            <span className="text-muted">-</span>
                        </td>
                        <td className="total-cell">
                            <span className="total-price">RM {parseFloat(item?.total_price || 0).toFixed(2)}</span>
                        </td>
                    </tr>
                );
            }

            return batches.map((batch, batchIndex) => (
                <tr key={`${itemIndex}-${batchIndex}`} className="order-item-row">
                    {batchIndex === 0 && (
                        <>
                            <td rowSpan={batches.length} className="item-cell">
                                <div className="d-flex align-items-center">
                                    <div>
                                        <div className="item-name">{item?.item_name || 'N/A'}</div>
                                        <small className="text-muted">{item?.ItemGroup || 'No Brand'}</small>
                                        <br />
                                        <small className="text-muted">Code: {item?.item_code || 'N/A'}</small>
                                    </div>
                                </div>
                            </td>
                            <td rowSpan={batches.length} className="quantity-cell">
                                <Badge style={{ backgroundColor: '#5689A2' }} className="quantity-badge">{item?.quantity || 0}</Badge>
                            </td>
                            <td rowSpan={batches.length} className="price-cell">
                                <span className="price-value">RM {parseFloat(item?.price || 0).toFixed(2)}</span>
                            </td>
                        </>
                    )}
                    <td className="batch-cell">
                        <Badge style={{ backgroundColor: '#3989A3' }} className="batch-badge">{batch.batch_number || '-'}</Badge>
                    </td>
                    <td className="expiry-cell">
                        {batch.expiry ? (
                            <span className="expiry-date">{moment(batch.expiry).format('DD-MM-YYYY')}</span>
                        ) : (
                            <span className="text-muted">-</span>
                        )}
                    </td>
                    <td className="total-cell">
                        <span className="total-price">RM {parseFloat(item?.total_price || 0).toFixed(2)}</span>
                    </td>
                </tr>
            ));
        });
    };

    const renderCustomerDetails = () => {
        const deliveryAddress = orderData?.orderDetails?.Addresses?.[0] || orderData?.deliveryAddress;
        const lat = deliveryAddress?.latitude;
        const lng = deliveryAddress?.longitude;
        const { location, unit, block } = deliveryAddress || {};
        const mapUrl = lat && lng ? `https://www.google.com/maps?q=${lat},${lng}` : '#';
        
        return (
            <Card className="main-card mb-3">
                <CardBody>
                    <h5 className="card-title mb-3">
                        <FontAwesomeIcon icon={faUser} style={{ color: '#3989A3' }} className="me-2" />
                        Customer Information
                    </h5>
                    <div className="customer-info-grid">
                        <div className="info-item">
                            <div className="info-icon">
                                <FontAwesomeIcon icon={faUser} style={{ color: '#3989A3' }} />
                            </div>
                            <div className="info-content">
                                <label>Customer Name</label>
                                <span>{orderData?.userDetails?.[0]?.name || 'N/A'}</span>
                            </div>
                        </div>
                        <div className="info-item">
                            <div className="info-icon">
                                <FontAwesomeIcon icon={faPhone} style={{ color: '#3D5F7A' }} />
                            </div>
                            <div className="info-content">
                                <label>Phone Number</label>
                                <span>{orderData?.userDetails?.[0]?.phone || 'N/A'}</span>
                            </div>
                        </div>
                        <div className="info-item">
                            <div className="info-icon">
                                <FontAwesomeIcon icon={faCheckCircle} style={{ color: '#5689A2' }} />
                            </div>
                            <div className="info-content">
                                <label>Customer Rating</label>
                                <span>{orderData?.userDetails?.[0]?.customer_rating || 'N/A'}</span>
                            </div>
                        </div>
                        <div className="info-item">
                            <div className="info-icon">
                                <FontAwesomeIcon icon={faBuilding} style={{ color: '#3989A3' }} />
                            </div>
                            <div className="info-content">
                                <label>Business</label>
                                <span>{orderData?.businessDetails?.[0]?.name || 'N/A'}</span>
                            </div>
                        </div>
                        {(orderData?.orderDetails?.deliveryMethod || orderData?.deliveryMethod) === 'delivery' && (
                            <div className="info-item full-width">
                                <div className="info-icon">
                                    <FontAwesomeIcon icon={faMapMarkerAlt} style={{ color: '#3D5F7A' }} />
                                </div>
                                <div className="info-content">
                                    <label>Delivery Address</label>
                                    <span>{`${location || 'N/A'}, ${unit || 'N/A'}, ${block || 'N/A'}`}</span>
                                    {lat && lng && (
                                        <NavLink target="_blank" to={mapUrl} className="map-link">
                                            <FontAwesomeIcon icon={faMapMarkerAlt} className="me-1" />
                                            View on Map
                                        </NavLink>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                </CardBody>
            </Card>
        );
    };

    const formatCurrency = (value) => {
        const parsedValue = parseFloat(value);
        return !isNaN(parsedValue)
            ? parsedValue.toFixed(2)
            : '0.00';
    };

    const renderOrderTotal = () => (
        <Card className="main-card mb-3">
            <CardBody>
                <h5 className="card-title mb-3">
                    <FontAwesomeIcon icon={faCreditCard} style={{ color: '#3989A3' }} className="me-2" />
                    Order Summary
                </h5>
                <div className="order-summary-grid">
                    <div className="summary-row">
                        <span className="summary-label">Order Sub Total:</span>
                        <span className="summary-value">RM {formatCurrency(orderData?.orderDetails?.Calculations?.subTotal || orderData?.Calculations?.subTotal)}</span>
                    </div>
                    <div className="summary-row">
                        <span className="summary-label">Delivery Charges:</span>
                        <span className="summary-value">RM {formatCurrency(orderData?.orderDetails?.Calculations?.deliveryTipFee || orderData?.Calculations?.deliveryTipFee)}</span>
                    </div>
                    <div className="summary-row">
                        <span className="summary-label">Discount:</span>
                        <span className="summary-value text-success">-RM {formatCurrency(orderData?.orderDetails?.Calculations?.discount || orderData?.Calculations?.discount)}</span>
                    </div>
                    <div className="summary-row">
                        <span className="summary-label">{(orderData?.orderDetails?.Calculations?.sst_dynamic_label || orderData?.Calculations?.sst_dynamic_label) || 'SST'} ({(orderData?.orderDetails?.Calculations?.sst_percentage || orderData?.Calculations?.sst_percentage) || '5%'}):</span>
                        <span className="summary-value">RM {formatCurrency(orderData?.orderDetails?.Calculations?.sst || orderData?.Calculations?.sst)}</span>
                    </div>
                    <div className="summary-row total-row">
                        <span className="summary-label">Grand Total:</span>
                        <span className="summary-value grand-total">RM {formatCurrency(orderData?.orderDetails?.Calculations?.grandTotal || orderData?.Calculations?.grandTotal)}</span>
                    </div>

                    {(orderData?.orderDetails?.deliveryMethod || orderData?.deliveryMethod) === 'delivery' && (
                        <>
                            <div className="summary-row">
                                <span className="summary-label text-danger">Additional Priority/Delivery Fee by Seller:</span>
                                <span className="summary-value text-danger">RM {formatCurrency(orderData?.orderDetails?.Calculations?.additionalDeliveryFeeBySeller || orderData?.Calculations?.additionalDeliveryFeeBySeller)}</span>
                            </div>
                            <div className="summary-row">
                                <span className="summary-label">Additional Delivery Fee by Customer:</span>
                                <span className="summary-value">RM {formatCurrency(orderData?.orderDetails?.Calculations?.additionalDeliveryFee || orderData?.Calculations?.additionalDeliveryFee)}</span>
                            </div>
                        </>
                    )}
                </div>
            </CardBody>
        </Card>
    );

    return (
        <div className="app-main__outer">
            <div className="app-main__inner">
                <LoadingOverlay active={loading} spinner={<DNALoader />}>
                    <div className="app-page-title">
                        <div className="page-title-wrapper">
                            <div className="page-title-heading">
                                <div className="page-title-icon">
                                    <FontAwesomeIcon icon={faClipboardList} className="icon-gradient bg-tempting-azure" />
                                </div>
                                <div>
                                    {t("Orders Management")}
                                    <div className="page-title-subheading">
                                        {t("Order Details & Tracking")}
                                    </div>
                                </div>
                            </div>
                            <div className="page-title-actions">
                                <Button 
                                    style={{ backgroundColor: '#5689A2', borderColor: '#5689A2' }}
                                    onClick={() => navigate(-1)} 
                                    className="btn-hover-shine me-3"
                                >
                                    <FontAwesomeIcon icon={faClock} className="me-2" />
                                    {t("Back")}
                                </Button>
                            </div>
                        </div>
                    </div>

                    {renderOrderHeader()}

                    <div className="order-tabs-container mb-3">
                        <div className="order-tabs">
                        <button
                                className={`tab-button ${activeTab === 'details' ? 'active' : ''}`}
                            onClick={() => setActiveTab('details')}
                        >
                                <FontAwesomeIcon icon={faClipboardList} className="me-2" />
                            Order Details
                        </button>
                        <button
                                className={`tab-button ${activeTab === 'tracking' ? 'active' : ''}`}
                            onClick={() => setActiveTab('tracking')}
                        >
                                <FontAwesomeIcon icon={faShippingFast} className="me-2" />
                            Order Tracking
                        </button>
                        <button
                            className={`tab-button ${activeTab === 'messaging' ? 'active' : ''}`}
                            onClick={() => {
                                setActiveTab('messaging');
                                if (orderData) {
                                    fetchMessages();
                                }
                            }}
                        >
                            <FontAwesomeIcon icon={faCommentAlt} className="me-2" />
                            Messaging
                        </button>
                        <button
                                className={`tab-button ${activeTab === 'payment_details' ? 'active' : ''}`}
                            onClick={() => setActiveTab('payment_details')}
                        >
                                <FontAwesomeIcon icon={faCreditCard} className="me-2" />
                            Payment Details
                        </button>
                    </div>
                    </div>

                    {activeTab === 'messaging' && renderMessagingTab()}
                    {activeTab !== 'payment_details' && (
                        <>
                            {activeTab === 'details' && renderOrderDetails()}
                            {activeTab === 'tracking' && renderTrackingContentDEMO()}
                        </>
                    )}

                    <Row className="mt-3">
                        <Col md="6">
                            {renderCustomerDetails()}
                        </Col>
                        <Col md="6">
                            {renderOrderTotal()}
                        </Col>
                    </Row>

                    {(orderData?.orderDetails?.status || orderData?.status) === 'pending' && (
                        <Card className="main-card mb-3">
                            <CardBody className="text-center">
                                <h6 className="mb-3">Order Actions</h6>
                                <div className="action-buttons">
                                    <Button 
                                        style={{ backgroundColor: '#3989A3', borderColor: '#3989A3' }}
                                        className="me-3" 
                                        onClick={(e) => processOrder(e, true)}
                                    >
                                        <FontAwesomeIcon icon={faTruck} className="me-2" />
                                        Process Order with Lalamove
                                    </Button>
                                    <Button 
                                        style={{ backgroundColor: '#3D5F7A', borderColor: '#3D5F7A' }}
                                        onClick={(e) => processOrder(e, false)}
                                    >
                                        <FontAwesomeIcon icon={faClipboardList} className="me-2" />
                                        Process Order without Lalamove
                                    </Button>
                                </div>
                            </CardBody>
                        </Card>
                    )}

                    {orderData?.late_status === true && orderData?.lalamove_orderID && orderData?.status === 'confirmed' && (
                        <Card className="main-card mb-3">
                            <CardBody className="text-center">
                                <Button 
                                    style={{ backgroundColor: '#dc3545', borderColor: '#dc3545' }}
                                    onClick={(e) => cancelLalamove(e)}
                                >
                                    <FontAwesomeIcon icon={faTruck} className="me-2" />
                                    Cancel Lalamove Only
                                </Button>
                            </CardBody>
                        </Card>
                    )}

                    {orderData?.lalamove_canceStatus === true && (
                        <Card className="main-card mb-3">
                            <CardBody>
                                <Row>
                                    <Col md="6" className="text-center">
                                        <Button 
                                            style={{ backgroundColor: '#3989A3', borderColor: '#3989A3' }}
                                            onClick={(e) => processOrder(e, true)}
                                        >
                                            <FontAwesomeIcon icon={faTruck} className="me-2" />
                                            Book Lalamove Only
                                        </Button>
                                    </Col>
                                    <Col md="6">
                                        <div className="lalamove-link-section">
                                            <h6 className="mb-2">Link Lalamove ID</h6>
                                    <InputGroup>
                                        <Input
                                                    placeholder="Enter Lalamove ID"
                                            value={inputValueLink}
                                            onChange={handleInputLinkChange}
                                                    className="text-center"
                                        />
                                        <InputGroupText addonType="append">
                                                    <Button style={{ backgroundColor: '#5689A2', borderColor: '#5689A2' }} onClick={applyLink}>
                                                        <FontAwesomeIcon icon={faLink} className="me-1" />
                                                        Link
                                                    </Button>
                                        </InputGroupText>
                                    </InputGroup>
                                </div>
                                    </Col>
                            </Row>
                            </CardBody>
                        </Card>
                    )}
                </LoadingOverlay>
            </div>
        </div>
    );
};

export default OrderDetails;